import React, { FC, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import EmbedModal, { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import analytics from '@ad-scripts/lib/analytics';
import ivrResource from 'src/actions/ivrs/ivrResource';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { IvrConfiguration } from './ivrTree/config/ivrConfig';
import { validateCommentRequire } from 'src/presentation/components/pages/ivr/helpers/commentRequired';
import { TextareaWithCharsCounter } from 'src/presentation/components/pages/shared/TextareaWithCharsCounter';

function useOnPublish(setIsSaveInProgress: Function, closeModal?: Function) {
  const dispatch = useDispatch();
  const { promote } = ivrsResource.effects;

  return ({ workingState }) => {
    if (dispatch(validateCommentRequire(workingState.comment))) {
      setIsSaveInProgress(true);
      analytics.track('Admin - Add publish description - Save');
      dispatch(promote({ id: workingState.id, version: workingState.version, comment: workingState.comment }));
      closeModal && closeModal();
    }
  };
}

const EditForm = ({ formContext }) => (
  <FormContainer>
    <Form context={formContext} preventSaveOnEnter>
      <InputDescriptionContainer>
        <TextareaWithCharsCounter
          autoFocus={true}
          attr="comment"
          data-aid="comment"
          placeholder={'Add a short description of your changes'}
          maxLength={100}
        />
      </InputDescriptionContainer>
    </Form>
  </FormContainer>
);

type PublishDescriptionProps = {
  errors?: any;
  item: IvrConfiguration;
  onCancel?: Function;
  setIsSaveInProgress: Function;
};

export const PublishDescriptionModal: FC<PublishDescriptionProps> = ({
  errors,
  item,
  onCancel,
  setIsSaveInProgress,
}) => {
  const { validate } = ivrResource.effects;
  const { resetErrors } = ivrResource.actions;

  const initializedItem = { ...item, comment: '' };
  const publishHandler = useOnPublish(setIsSaveInProgress, onCancel);
  const dispatch = useDispatch();

  const handleCancel = () => {
    analytics.track('Admin - Add publish description - Canceled');
    dispatch(resetErrors({}));
    onCancel && onCancel();
  };

  const formContext = useForm({
    errors,
    item: initializedItem,
    onSave: publishHandler,
    resetErrors,
    validate,
  });

  const { onSave } = formContext;

  return (
    <StyledEmbedModal
      data-aid="publish-comment-modal"
      title="What changes did you make?"
      content={EmbedModalContent({ context: formContext, onCancel: handleCancel, onSave })}
      onCancel={handleCancel}
      modalSize={ModalSize.DEFAULT}
    />
  );
};

const EmbedModalContent = ({ context, onCancel, onSave }) => {
  return (
    <Fragment>
      <EditForm formContext={context} />
      <StyledFooter>
        <Button data-aid={'editor-cancel'} type={Button.Types.TEXT} onClick={onCancel}>
          Cancel
        </Button>
        <Button data-aid={'editor-save'} onClick={onSave}>
          OK
        </Button>
      </StyledFooter>
    </Fragment>
  );
};

const FormContainer = styled.div`
  text-align: left;
`;

const StyledFooter = styled(Footer)`
  justify-content: center;
  padding: 0 0 16px 0;
`;

const StyledEmbedModal = styled(EmbedModal)`
  .modalCard-backdrop {
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
  }

  .modalCard-content {
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.04), 0px 0px 10px rgba(0, 0, 0, 0.06), 0px 0px 80px rgba(0, 0, 0, 0.06);
    padding: 0 30px;
  }
`;

const InputDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding-bottom: 5px;

  input {
    min-width: 360px;
  }

  [data-aid='comment-error'] {
    position: absolute;
    top: 35px;
  }
`;
